import { Provider } from 'react-redux'
import { BaseQueryFn } from '@reduxjs/toolkit/query'
import { Router, RouterProps, Route, Switch } from 'react-router'

import {
  LocalStorageProvider,
  Analytics,
  AnalyticsProvider,
  PowerProAPIProvider,
  ID,
} from '~/service'
import { StoreFactory } from '~/store'
import { history as defaultHistory } from '../history'
import React from 'react'
import { PageLoader } from '~/components/loader'
import { Toasts } from '~/components/toasts'
import { color } from 'console-log-colors'
import { logFactory } from '~/util/logger'

/**
 * Dynamically import a module with a default export and return the default
 * export.
 *
 * @example
 * ```ts
 * const MyComponent = useModule(() => import('./MyComponent'))
 * ```
 */
function useModule<DefaultExport>(
  getModule: () => Promise<{ default: DefaultExport }>
) {
  const [Module, setModule] = React.useState<DefaultExport>()

  React.useEffect(
    () => {
      getModule().then((module) => {
        setModule(() => module.default)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return Module
}

type TourToGoMain = typeof import('~/pages/tour-to-go/Main')['default']
type TourToGoProps = import('~/pages/tour-to-go/Main').TourToGoProps

function TourToGo(props: TourToGoProps) {
  const ModuleMain = useModule<TourToGoMain>(
    () => import('~/pages/tour-to-go/Main')
  )

  if (!ModuleMain) {
    return <PageLoader />
  } else {
    return <ModuleMain {...props} />
  }
}

type SelfTourMain = typeof import('~/pages/self-tour/Main')['default']
type SelfTourProps = import('~/pages/self-tour/Main').SelfTourProps

function SelfTour(props: SelfTourProps) {
  const ModuleMain = useModule<SelfTourMain>(
    () => import('~/pages/self-tour/Main')
  )

  if (!ModuleMain) {
    return <PageLoader />
  } else {
    return <ModuleMain {...props} />
  }
}

type EasyTourMain = typeof import('~/pages/easy-tour/Main')['default']
type EasyTourProps = import('~/pages/easy-tour/Main').EasyTourProps

function EasyTour(props: EasyTourProps) {
  const ModuleMain = useModule<EasyTourMain>(
    () => import('~/pages/easy-tour/Main')
  )

  if (!ModuleMain) {
    return <PageLoader />
  } else {
    return <ModuleMain {...props} />
  }
}

const logger = logFactory('History', color.bold.magenta)

const DebugRouter = ({ history, ...rest }: RouterProps) => {
  React.useEffect(() => {
    logger.log('init:', JSON.stringify(history, null, 2))

    const removeListener = history.listen((location, action) => {
      logger.log(
        `${action} ${location.pathname}${location.search}${location.hash}`,
        'history:',
        JSON.stringify(history, null, 2)
      )
    })

    return () => removeListener()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Router history={history} {...rest} />
}

interface AppRouterProps {
  history?: typeof defaultHistory
}

export const AppRouter = React.memo(({ history }: AppRouterProps) => {
  return (
    <DebugRouter history={history || defaultHistory}>
      <Switch>
        <Route path="/easy-tour">
          <EasyTour />
        </Route>
        <Route path={['/:uuid/self-tour', '/:uuid/verify-identity']}>
          <SelfTour />
        </Route>
        <Route path="/">
          <TourToGo />
        </Route>
      </Switch>
    </DebugRouter>
  )
})

export interface WithServerProps extends AppRouterProps {
  guestCardUuid?: ID
  localStorage?: Storage
  location?: Location
  analytics?: Analytics
  powerProAPI?: BaseQueryFn
  retryCount?: number
  trackingId?: string
  trackingOptions?: Record<string, any>
}

export function WithServer({
  localStorage = window.localStorage,
  analytics: _analytics,
  trackingId,
  trackingOptions,
  history,
  powerProAPI: _powerProAPI,
  retryCount,
}: WithServerProps) {
  const analytics = React.useMemo(
    () => _analytics ?? new Analytics(trackingId, trackingOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const { store, PowerProAPI } = React.useMemo(
    () => {
      return StoreFactory(_powerProAPI, retryCount)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <AnalyticsProvider value={analytics}>
      <LocalStorageProvider value={localStorage}>
        <Provider store={store}>
          <PowerProAPIProvider value={PowerProAPI}>
            <AppRouter history={history} />
            {/* Inside the providers because toast bodies can use them, e.g.
                `Action` tracks clicks through the analytics client. */}
            <Toasts />
          </PowerProAPIProvider>
        </Provider>
      </LocalStorageProvider>
    </AnalyticsProvider>
  )
}
